/**
 * Cognito hosted UI auth with Google federation. Tokens are kept in memory
 * only — per spec, they are NEVER persisted to localStorage or sessionStorage.
 * If the user refreshes the page, they are redirected back through Cognito
 * (silent refresh if session is still valid server-side).
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { RuntimeConfig } from "../runtime-config";

export interface AuthUser {
  sub: string;
  email?: string;
  name?: string;
  idToken: string;
  accessToken: string;
  expiresAt: number;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function buildUserManager(cfg: RuntimeConfig): UserManager {
  return new UserManager({
    authority: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`,
    client_id: cfg.userPoolClientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: "code",
    scope: "openid email profile",
    // Storage policy:
    //   userStore (caches the User object including tokens): in-memory. Tokens
    //     evaporate on reload — by design, per spec ("no localStorage/sessionStorage
    //     for tokens"). The user re-auths after a page refresh.
    //   stateStore (PKCE state + nonce, transient values during the redirect
    //     flow): sessionStorage. MUST survive the full-page navigation away
    //     to Cognito and back, or signinRedirectCallback() will fail to match
    //     state and the callback handler will hang silently. sessionStorage
    //     auto-clears when the tab closes, so no long-term residue.
    userStore: new WebStorageStateStore({ store: inMemoryStore() }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    metadata: {
      issuer: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}`,
      authorization_endpoint: `https://${cfg.cognitoDomain}/oauth2/authorize`,
      token_endpoint: `https://${cfg.cognitoDomain}/oauth2/token`,
      userinfo_endpoint: `https://${cfg.cognitoDomain}/oauth2/userInfo`,
      end_session_endpoint: `https://${cfg.cognitoDomain}/logout`,
      jwks_uri: `https://cognito-idp.${cfg.region}.amazonaws.com/${cfg.userPoolId}/.well-known/jwks.json`,
    },
    // Always federate through Google — no email/password form
    extraQueryParams: { identity_provider: "Google" },
  });
}

/** Ephemeral Storage adapter — evaporates on reload. */
function inMemoryStore(): Storage {
  const m = new Map<string, string>();
  return {
    length: 0,
    clear: () => m.clear(),
    getItem: (k: string) => m.get(k) ?? null,
    key: (i: number) => Array.from(m.keys())[i] ?? null,
    removeItem: (k: string) => { m.delete(k); },
    setItem: (k: string, v: string) => { m.set(k, v); },
  } as Storage;
}

export function AuthProvider({ cfg, children }: { cfg: RuntimeConfig; children: React.ReactNode }) {
  const [um] = useState(() => buildUserManager(cfg));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Handle callback?
        if (window.location.pathname === "/auth/callback") {
          const u = await um.signinRedirectCallback();
          commit(u);
          window.history.replaceState({}, "", "/");
          return;
        }
        const existing = await um.getUser();
        if (existing && !existing.expired) commit(existing);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[auth] init failed", e);
      } finally {
        setLoading(false);
      }
    })();

    function commit(u: Awaited<ReturnType<UserManager["getUser"]>>) {
      if (!u) return;
      const profile = u.profile as { sub: string; email?: string; name?: string };
      setUser({
        sub: profile.sub,
        email: profile.email,
        name: profile.name,
        idToken: u.id_token ?? "",
        accessToken: u.access_token,
        expiresAt: (u.expires_at ?? 0) * 1000,
      });
    }

    um.events.addUserLoaded((u) => commit(u));
    um.events.addUserUnloaded(() => setUser(null));
    um.events.addSilentRenewError((e) => {
      // eslint-disable-next-line no-console
      console.warn("[auth] silent renew failed", e);
    });
  }, [um]);

  const signIn = () => void um.signinRedirect();
  const signOut = async () => {
    setUser(null);
    await um.removeUser();
    await um.signoutRedirect();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
